import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, Menu, MenuItem, Tab, Tabs } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ProgressBar from "../../UI/ProgressBar";
import { useAlert } from "../../../hooks/useAlert";
import {
  useGetPromotionEmailTemplatesQuery,
  usePreviewPromotionEmailTemplateMutation,
  useResetPromotionEmailTemplateMutation,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { formatTemplateModifiedAt } from "./emailTemplateDisplay";
import { EmailTemplatePreviewModal } from "./EmailTemplatePreviewModal";
import { EmailTemplateResetModal } from "./EmailTemplateResetModal";
import { EmailSettingsPanel } from "./EmailSettingsPanel";
import "./Promotions.css";
import "./EmailTemplates.css";

const TABS = [
  { value: "book", label: "Templates" },
  { value: "settings", label: "Settings" },
];

export const EmailTemplates = () => {
  const navigate = useNavigate();
  const { displayAlert } = useAlert();
  const [tab, setTab] = useState("book");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [preview, setPreview] = useState(null);
  const [resetRow, setResetRow] = useState(null);

  const { data, isLoading, isFetching, error } =
    useGetPromotionEmailTemplatesQuery(undefined, {
      skip: tab === "settings",
      refetchOnMountOrArgChange: true,
    });
  const [previewTemplate, { isLoading: isPreviewing }] =
    usePreviewPromotionEmailTemplateMutation();
  const [resetTemplate, { isLoading: isResetting }] =
    useResetPromotionEmailTemplateMutation();

  const templates = data?.data?.templates || [];

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const openMenu = (event, row) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const handlePreview = async (row = menuRow) => {
    closeMenu();
    if (!row?._id) return;
    try {
      const result = await previewTemplate({ templateId: row._id }).unwrap();
      setPreview({
        title: row.templateName,
        subject: result?.data?.preview?.subject,
        html: result?.data?.preview?.html,
      });
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to preview template",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const handleEdit = (row = menuRow) => {
    closeMenu();
    if (!row?._id) return;
    navigate(`/super-admin/promotions/email-templates/${row._id}`);
  };

  const handleReset = (row = menuRow) => {
    closeMenu();
    if (!row?._id) return;
    setResetRow(row);
  };

  const confirmReset = async () => {
    if (!resetRow?._id) return;
    try {
      await resetTemplate({ templateId: resetRow._id }).unwrap();
      displayAlert({
        open: true,
        message: `${resetRow.templateName} was reset to the default template`,
        type: "success",
        timeout: "2500",
      });
      setResetRow(null);
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to reset template",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const emptyCopy = "No organisation book templates are available yet.";

  return (
    <div className="promotions-page container">
      <div className="promotions-page__tabs-row">
        <Tabs
          value={tab}
          onChange={(_e, value) => setTab(value)}
          className="promotions-page__tabs"
        >
          {TABS.map((item) => (
            <Tab key={item.value} label={item.label} value={item.value} />
          ))}
        </Tabs>
      </div>

      {tab === "settings" ? (
        <EmailSettingsPanel />
      ) : (
      <div className="promotions-page__table-wrap email-templates-table-wrap">
        {isLoading || isFetching ? (
          <div className="email-templates-table__loading">
            <ProgressBar />
          </div>
        ) : error ? (
          <p className="promotions-table__empty">
            {error?.data?.message || "Unable to load email templates"}
          </p>
        ) : (
          <table className="promotions-table email-templates-table">
            <colgroup>
              <col className="email-templates-table__col-name" />
              <col className="email-templates-table__col-modified-by" />
              <col className="email-templates-table__col-modified-at" />
              <col className="email-templates-table__col-action" />
            </colgroup>
            <thead>
              <tr>
                <th>Template Name</th>
                <th>Modified by</th>
                <th>Last Modified</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={4} className="promotions-table__empty">
                    {emptyCopy}
                  </td>
                </tr>
              ) : (
                templates.map((row) => (
                  <tr key={row._id || row.bookId}>
                    <td>
                      <span className="email-templates-table__name">
                        {row.templateName || "—"}
                      </span>
                    </td>
                    <td>{row.modifiedBy || "System"}</td>
                    <td>{formatTemplateModifiedAt(row.lastModified)}</td>
                    <td className="email-templates-table__action">
                      <IconButton
                        size="small"
                        aria-label="Template actions"
                        onClick={(e) => openMenu(e, row)}
                        disabled={!row._id}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "promo-history-actions-menu" }}
      >
        <MenuItem onClick={() => handlePreview()}>Preview</MenuItem>
        <MenuItem onClick={() => handleEdit()}>Edit Template</MenuItem>
        <MenuItem onClick={() => handleReset()}>Reset to Default</MenuItem>
      </Menu>

      <EmailTemplatePreviewModal
        open={Boolean(preview) || isPreviewing}
        onClose={() => setPreview(null)}
        title={preview?.title ? `Preview Email : ${preview.title}` : "Email preview"}
        subject={preview?.subject}
        html={preview?.html}
        loading={isPreviewing && !preview}
      />

      <EmailTemplateResetModal
        open={Boolean(resetRow)}
        onClose={() => {
          if (isResetting) return;
          setResetRow(null);
        }}
        onConfirm={confirmReset}
        confirming={isResetting}
        templateName={resetRow?.templateName}
      />
    </div>
  );
};
